import { Color } from "@brookbush/database";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRightIcon, MinusIcon, Trash2Icon } from "lucide-react";
import Link from "next/link";
import { useEffect } from "react";

import { useAccess, useGlobalStore } from "~/hooks";
import useHighlights from "~/hooks/useHighlights";
import useKeyboardShortcut from "~/hooks/useKeyboardShortcut";
import { getRoutePrefix } from "~/utils/helpers";

interface Props {
	hasBreadcrumbs?: boolean;
}

const AnnotationsPanel = ({ hasBreadcrumbs }: Props) => {
	const { isAnnotationsPanelOpen, toggleAnnotationsPanel } = useGlobalStore();
	const { isMember } = useAccess();

	const { highlights, removeHighlight } = useHighlights({ highlight: true });

	// biome-ignore lint/correctness/useExhaustiveDependencies: needed
	useEffect(() => {
		return () => {
			document.body.style.overflow = "auto";
		};
	}, [isAnnotationsPanelOpen]);

	useKeyboardShortcut("i", toggleAnnotationsPanel);

	return (
		<AnimatePresence mode="wait">
			{isAnnotationsPanelOpen && (
				<motion.div
					onMouseEnter={() => {
						document.body.style.overflow = "hidden";
					}}
					onMouseLeave={() => {
						document.body.style.overflow = "auto";
					}}
					initial={{
						opacity: 0,
						x: 100,
					}}
					animate={{
						opacity: 1,
						x: 0,

						transition: {
							duration: 0.2,
						},
					}}
					exit={{
						opacity: 0,
						x: 100,
						transition: {
							duration: 0.1,
						},
					}}
					className={clsx(
						"fixed top-0 laptop:right-0 laptop:left-auto left-0 laptop:z-10 z-50 h-[100dvh] w-screen laptop:max-w-md overflow-hidden laptop:border bg-white shadow-slate-950/10 dark:bg-slate-800 dark:shadow-slate-950/50",
						isMember
							? hasBreadcrumbs
								? "laptop:top-[6.25rem] laptop:h-[calc(100dvh-6.25rem)]"
								: "laptop:top-[3.95rem] laptop:h-[calc(100dvh-3.95rem)]"
							: hasBreadcrumbs
								? "laptop:top-[6.25rem] laptop:h-[calc(100dvh-10.35rem)]"
								: "laptop:top-[3.95rem] laptop:h-[calc(100dvh-8.05rem)]",
					)}
				>
					<div className="relative -mr-1 flex items-center justify-between bg-gradient-to-b from-white via-white to-transparent px-4 py-2.5 dark:from-slate-800 dark:via-slate-800 dark:to-transparent">
						<h2 className="font-semibold text-xl">Highlights</h2>

						<button
							onClick={toggleAnnotationsPanel}
							className="flex rounded-lg p-1.5 hover:bg-black/5 dark:text-white dark:hover:bg-white/10"
						>
							<MinusIcon size={22} />
						</button>
					</div>

					<div className="-mt-8 flex h-full flex-col items-center justify-start overflow-auto px-4 pt-8 pb-10">
						{highlights?.length ? (
							<div className="flex flex-col gap-6">
								{highlights.map((highlight) => (
									<div key={highlight.id} className="flex justify-start gap-3">
										<div
											className={clsx(
												"h-auto w-1 shrink-0 rounded-lg",
												highlight.color === Color.RED
													? "bg-red-300 dark:bg-red-300/20"
													: highlight.color === Color.YELLOW
														? "bg-amber-300 dark:bg-amber-300/20"
														: highlight.color === Color.GREEN
															? "bg-green-300 dark:bg-green-300/20"
															: "bg-slate-300 dark:bg-slate-500/20",
											)}
										/>

										<div className={clsx("flex flex-col gap-2 text-sm")}>
											<Link
												href={`/${getRoutePrefix(highlight.contentType)}/${
													highlight.content?.slug
												}#highlight-${highlight.id}`}
												className="group font-medium capitalize hover:underline"
											>
												{highlight.contentType.toLocaleLowerCase()}:{" "}
												{highlight.content?.title}{" "}
												<ArrowUpRightIcon className="inline-block size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
											</Link>

											<marker
												className={clsx(
													"rounded-lg px-2.5 py-1",
													highlight.color === Color.RED
														? "dark:!bg-red-300/20 bg-red-200"
														: highlight.color === Color.YELLOW
															? "dark:!bg-amber-300/20 bg-amber-200"
															: highlight.color === Color.GREEN
																? "dark:!bg-green-300/20 bg-green-200"
																: "bg-slate-300 dark:bg-slate-500",
												)}
											>
												{" "}
												{highlight.text}
											</marker>

											{highlight.note && (
												<blockquote className="flex gap-1 italic">
													{" "}
													&gt; <span>{highlight.note?.body}</span>
												</blockquote>
											)}

											<div className="flex items-center justify-between">
												<p className="text-slate-500">
													{new Date(highlight.createdAt).toLocaleDateString(
														"en-US",
														{ dateStyle: "medium" },
													)}
												</p>

												<button
													onClick={(e) => {
														e.stopPropagation();
														removeHighlight(highlight.id);
													}}
													className="flex items-center gap-1 font-medium hover:opacity-50"
												>
													<Trash2Icon className="size-4" /> Delete
												</button>
											</div>
										</div>
									</div>
								))}
							</div>
						) : (
							<p className="flex flex-1 items-center justify-center text-center text-slate-500">
								No highlights or notes found.
							</p>
						)}

						<div className="mt-4 text-slate-500 text-xs">
							You can highlight text by selecting it and clicking the highlight
							button.
						</div>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default AnnotationsPanel;
